import { createSelector } from '@reduxjs/toolkit';
import _, { isNil } from 'lodash';
import type { RootState } from 'src/root/store';

import { PendingItemType } from '@appTypes/enums';
import { AdventureV1, MeritBadgeV1, RankV1 } from '@appTypes/esb';
import { AwardV1 } from '@appTypes/esb/advancements/youth/awards';
import { mapAdvancementHistoryDtoToModel } from '@modules/advancement/advancementHistory/utils';
import { toPurchaseStatusesArray } from '@modules/advancement/constants';
import { userSubUnitMembersSel } from '@modules/advancement/packRoster';
import { isYouthInUserSubunitChecker } from '@modules/advancement/pendingItems/utils';
import { ProgramId, advancementTypes } from '@modules/shared/constants';
import {
  adventuresSel,
  allAwardsSel,
  meritBadgesSel,
  organizationGuidSel,
  packRosterItemsSel,
  ranksSel,
  shouldFilterUsersBySubunitsSel,
  unitTypeIdSel,
} from '@shared/duck/selectors/general.selectors';

import { purchaseOrdersModuleSel } from '../../common';
import { purchaseOrderApi } from '../api';
import type { GroupByToApproveTableOptions } from './types';

export const pendingItemsToPurchaseQueryPayloadSel = createSelector(
  unitTypeIdSel,
  organizationGuidSel,
  packRosterItemsSel,
  (unitTypeId, selectedOrgGuid, rosterItemsCount) => {
    const isPack = ProgramId.CUB_SCOUT === unitTypeId;
    const perPage = rosterItemsCount?.length || 100;
    const advTypes = isPack
      ? [
          PendingItemType.RANKS,
          PendingItemType.AWARDS,
          PendingItemType.ADVENTURES,
        ]
      : [
          PendingItemType.RANKS,
          PendingItemType.AWARDS,
          PendingItemType.MERIT_BADGES,
        ];

    return {
      advancementTypes: advTypes,
      payload: {
        body: {
          status: toPurchaseStatusesArray,
          showAdults: true,
          showYouths: true,
          organizationGuid: selectedOrgGuid,
        },
        params: {
          perPage,
        },
      },
    };
  },
);

const pendingItemsToPurchaseQuerySel = (state: RootState) => {
  const arg = pendingItemsToPurchaseQueryPayloadSel(state);

  const response =
    purchaseOrderApi.endpoints.getBulkAdvancementHistoryToPurchase.select(arg)(
      state,
    );

  return response;
};

export const pendingItemsToPurchaseSel = createSelector(
  adventuresSel,
  meritBadgesSel,
  ranksSel,
  allAwardsSel,
  pendingItemsToPurchaseQuerySel,
  shouldFilterUsersBySubunitsSel,
  userSubUnitMembersSel,
  // TODO: add awardsSel, elective seascouts stuff
  (
    adventuresDict,
    meritBadgesDict,
    ranksDict,
    awardDict,
    response,
    shouldFilterUsersBySubunits,
    userSubUnitMembers,
  ) => {
    const adventureList: Record<AdventureV1['id'], AdventureV1> = _.chain(
      adventuresDict,
    )
      .keyBy('id')
      .value();
    const meritBadgeList: Record<MeritBadgeV1['id'], MeritBadgeV1> = _.chain(
      meritBadgesDict,
    )
      .keyBy('id')
      .value();
    const rankList: Record<RankV1['id'], RankV1> = _.chain(ranksDict)
      .keyBy('id')
      .value();
    const awardList: Record<AwardV1['id'], AwardV1> = _.chain(awardDict)
      .keyBy('id')
      .value();

    const data = mapAdvancementHistoryDtoToModel(response?.data || []).map(
      item => {
        let imgUrl = '';

        switch (item?.advancementType) {
          case advancementTypes.ADVENTURES: {
            imgUrl = adventureList[item.id]?.imageUrl100;
            break;
          }
          case advancementTypes.MERIT_BADGES: {
            imgUrl = meritBadgeList[item.id]?.imageUrl100;
            break;
          }
          case advancementTypes.RANKS: {
            imgUrl = rankList[item.id]?.imageUrl100;
            break;
          }
          case advancementTypes.AWARDS: {
            imgUrl = awardList[item.id]?.imageUrl100;
            break;
          }
        }

        const userSubUnitUsersMIDs = (userSubUnitMembers ?? [])
          .map(member => member.memberId)
          .filter((memberId): memberId is number => !isNil(memberId));

        return {
          ...item,
          imgUrl,
          isUserInSubUnit: isYouthInUserSubunitChecker(
            shouldFilterUsersBySubunits,
            userSubUnitUsersMIDs,
            item.memberId,
          ),
        };
      },
    );

    return data;
  },
);

export type PurchaseOrderTableItem = ReturnType<
  typeof pendingItemsToPurchaseSel
>[0];

export const groupByToApproveTableSel = (state: RootState) =>
  purchaseOrdersModuleSel(state).sliceReducer
    .groupByToApproveTable as GroupByToApproveTableOptions;
